import { print } from 'graphql';

import { LIMIT_FOR_FETCHING_ADMIN_ITEMS, SORT_TYPE } from '~/shared/constants';
import {
  clientWorkspaceStatsQuery,
  createOneClientWorkspaceLeadMutation,
  findByIdClientWorkspaceLeadQuery,
  //findManyPartnerProductQuery,
  // leadSourcePartnersQuery,
  paginationClientWorkspaceLeadQuery,
  registerClientWorkspaceLeadInLmsMutation,
  updateByIdClientWorkspaceLeadMutation,
} from '~/shared/graphql-queries/client-workspace';
import { leadSourcePartnersQuery } from '~/shared/graphql-queries/client-workspace/lead-source-partners-query';
import { findByIdPartnerCompanyRegistrationQuery } from '~/shared/graphql-queries/client-workspace/partner-company-registration-query';
import { findManyPartnerProductQuery } from '~/shared/graphql-queries/client-workspace/partner-products-query';
import type { PageInfoType } from '~/shared/types/general-type';
import type { BuilderType } from '~/types';
export type PartnerCompanyRegistrationApiType = Readonly<{
  _id: string;
  title?: string | null;
  founderEmail?: string | null;
  founderPhone?: string | null;
  platformCompanyId?: string | null;
  referralCompanyId?: string | null;
  isActivated?: boolean | null;
  isRemoved?: boolean | null;
}>;

type FindByIdPartnerCompanyRegistrationResponse = Readonly<{
  data?: {
    findByIdPartnerCompanyRegistrationQuery?: PartnerCompanyRegistrationApiType | null;
  };
}>;

type FindByIdPartnerCompanyRegistrationArgs = Readonly<{
  _id?: string | null;
}>;
export type ClientWorkspaceLeadProjectionType = Readonly<{
  lmsStudentId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phoneMasked?: string | null;
  emailMasked?: string | null;
  //childName?: string | null;
  age?: number | null;
  studentStatus?: string | null;
  currentCourseId?: string | null;
  currentCourseTitle?: string | null;
  teacherId?: string | null;
  teacherName?: string | null;
  trialLessonAt?: string | null;
  lastPaymentStatus?: string | null;
  lastSyncedAt?: string | null;
}>;

export type ClientWorkspaceLeadProductType = Readonly<{
  productId?: string | null;
  productType?: string | null;
  rewardValue?: number | null;
  paymentType?: string | null;
  isActive?: boolean | null;
}>;

export type ClientWorkspaceLeadType = Readonly<{
  _id: string;
  company?: string | null;
  companyTitle?: string | null;
  platformCompanyId?: boolean | null;
  sourceType?: string | null;
  sourceId?: string | null;
  paymentRemainder?: number | null;
  fullPaymentAt?: string | null;
  bonusAvailableAt?: string | null;
  referralCode?: string | null;
  referrerId?: string | null;
  referrerName?: string | null;
  lmsStudentId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  email?: string | null;
  //childName?: string | null;
  age?: number | null;
  courseType?: string | null;
  productId?: string | null;
  productSource?: string | null;
  products?: ClientWorkspaceLeadProductType[] | null;
  status?: string | null;
  managerId?: string | null;
  signedForTrial?: string | null;
  trialDate?: string | null;
  trialResult?: string | null;
  lessonOneDate?: string | null;
  lessonOneResult?: string | null;
  paymentStatus?: string | null;
  paymentSum?: number | null;
  currency?: string | null;
  notes?: string | null;
  nextContactDate?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  projection?: ClientWorkspaceLeadProjectionType | null;
}>;

export type LeadSourcePartnerOptionType = Readonly<{
  _id: string;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
  email?: string | null;
  login?: string | null;
  phone?: string | null;
  partnerType?: string | null;
  referralCode?: string | null;
}>;

export type PartnerProductOptionApiType = Readonly<{
  _id: string;
  title?: string | null;
  productType?: string | null;
  isActive?: boolean | null;
}>;

type PaginationResponse = Readonly<{
  data?: { paginationClientWorkspaceLeadQuery?: { items: ClientWorkspaceLeadType[]; pageInfo: PageInfoType } };
}>;

type FindByIdResponse = Readonly<{ data?: { findByIdClientWorkspaceLeadQuery?: ClientWorkspaceLeadType | null } }>;

type StatsResponse = Readonly<{
  data?: {
    clientWorkspaceStatsQuery?: {
      totalClients: number;
      signedForTrial: number;
      firstLessonCompleted: number;
      paid: number;
      revenue: number;
    };
  };
}>;

type LeadSourcePartnersResponse = Readonly<{
  data?: {
    leadSourcePartnersQuery?: LeadSourcePartnerOptionType[];
  };
}>;

type PartnerProductsResponse = Readonly<{
  data?: {
    findManyPartnerProductQuery?: PartnerProductOptionApiType[];
  };
}>;

type PaginationArgs = Readonly<{
  filter?: Record<string, unknown>;
  nextPage?: number;
  perPage?: number;
  sort?: string;
}>;

type FindByIdArgs = Readonly<{ _id?: string }>;
type CreateArgs = Readonly<{ record: Record<string, unknown>; hideFlashMessage?: boolean }>;
type UpdateArgs = Readonly<{ _id: string; record: Record<string, unknown>; hideFlashMessage?: boolean }>;
type RegisterArgs = Readonly<{ leadId: string; course: Record<string, unknown>; hideFlashMessage?: boolean }>;
type LeadSourcePartnersArgs = Readonly<{ partnerType?: string | null; search?: string | null; limit?: number }>;
type PartnerProductsArgs = Readonly<{ filter?: Record<string, unknown> }>;

export const clientWorkspaceTagTypes = [
  'clientWorkspaceLeads',
  'clientWorkspaceLead',
  'clientWorkspaceStats',
  'leadSourcePartners',
  'partnerProducts',
  'partnerCompanyRegistration',
] as const;

export const getClientWorkspaceQuery = (builder: BuilderType) => ({
  paginationClientWorkspaceLead: builder.query<
    { items: ClientWorkspaceLeadType[]; pageInfo: PageInfoType },
    PaginationArgs
  >({
    query: (args) => ({
      url: '',
      method: 'POST',
      body: JSON.stringify({
        query: print(paginationClientWorkspaceLeadQuery),
        variables: {
          filter: args?.filter,
          page: args?.nextPage ?? 1,
          perPage: args?.perPage ?? LIMIT_FOR_FETCHING_ADMIN_ITEMS,
          sort: args?.sort ?? SORT_TYPE._ID_DESC,
        },
      }),
    }),
    providesTags: ['clientWorkspaceLeads'],
    transformResponse: (response: PaginationResponse) =>
      response.data?.paginationClientWorkspaceLeadQuery ?? { items: [], pageInfo: {} as PageInfoType },
  }),

  findByIdClientWorkspaceLead: builder.query<ClientWorkspaceLeadType | null, FindByIdArgs>({
    query: (args) => ({
      url: '',
      method: 'POST',
      body: JSON.stringify({ query: print(findByIdClientWorkspaceLeadQuery), variables: { _id: args._id } }),
    }),
    providesTags: ['clientWorkspaceLead'],
    transformResponse: (response: FindByIdResponse) => response.data?.findByIdClientWorkspaceLeadQuery ?? null,
  }),

  clientWorkspaceStats: builder.query<
    { totalClients: number; signedForTrial: number; firstLessonCompleted: number; paid: number; revenue: number },
    Record<string, unknown> | void
  >({
    query: (args) => ({
      url: '',
      method: 'POST',
      body: JSON.stringify({ query: print(clientWorkspaceStatsQuery), variables: args ?? {} }),
    }),
    providesTags: ['clientWorkspaceStats'],
    transformResponse: (response: StatsResponse) =>
      response.data?.clientWorkspaceStatsQuery ?? {
        totalClients: 0,
        signedForTrial: 0,
        firstLessonCompleted: 0,
        paid: 0,
        revenue: 0,
      },
  }),

  createOneClientWorkspaceLead: builder.mutation<unknown, CreateArgs>({
    query: ({ hideFlashMessage = false, ...args }) => ({
      url: '',
      method: 'POST',
      body: JSON.stringify({ query: print(createOneClientWorkspaceLeadMutation), variables: { record: args.record } }),
      meta: { hideFlashMessage },
    }),
    invalidatesTags: ['clientWorkspaceLeads', 'clientWorkspaceStats'],
  }),
  findByIdPartnerCompanyRegistration: builder.query<
    PartnerCompanyRegistrationApiType | null,
    FindByIdPartnerCompanyRegistrationArgs
  >({
    query: (args) => ({
      url: '',
      method: 'POST',
      body: JSON.stringify({
        query: print(findByIdPartnerCompanyRegistrationQuery),
        variables: {
          _id: args._id,
        },
      }),
    }),
    providesTags: ['partnerCompanyRegistration'],
    transformResponse: (response: FindByIdPartnerCompanyRegistrationResponse) =>
      response.data?.findByIdPartnerCompanyRegistrationQuery ?? null,
  }),
  updateByIdClientWorkspaceLead: builder.mutation<unknown, UpdateArgs>({
    query: ({ hideFlashMessage = false, ...args }) => ({
      url: '',
      method: 'POST',
      body: JSON.stringify({
        query: print(updateByIdClientWorkspaceLeadMutation),
        variables: { _id: args._id, record: args.record },
      }),
      meta: { hideFlashMessage },
    }),
    invalidatesTags: ['clientWorkspaceLeads', 'clientWorkspaceLead', 'clientWorkspaceStats'],
  }),

  registerClientWorkspaceLeadInLms: builder.mutation<unknown, RegisterArgs>({
    query: ({ hideFlashMessage = false, ...args }) => ({
      url: '',
      method: 'POST',
      body: JSON.stringify({
        query: print(registerClientWorkspaceLeadInLmsMutation),
        variables: { leadId: args.leadId, course: args.course },
      }),
      meta: { hideFlashMessage },
    }),
    invalidatesTags: ['clientWorkspaceLeads', 'clientWorkspaceLead', 'clientWorkspaceStats', 'users', 'user'],
  }),

  leadSourcePartners: builder.query<LeadSourcePartnerOptionType[], LeadSourcePartnersArgs>({
    query: (args) => ({
      url: '',
      method: 'POST',
      body: JSON.stringify({
        query: print(leadSourcePartnersQuery),
        variables: {
          partnerType: args.partnerType,
          search: args.search,
          limit: args.limit ?? 50,
        },
      }),
    }),
    providesTags: ['leadSourcePartners'],
    transformResponse: (response: LeadSourcePartnersResponse) => response.data?.leadSourcePartnersQuery ?? [],
  }),

  findManyPartnerProduct: builder.query<PartnerProductOptionApiType[], PartnerProductsArgs | void>({
    query: (args) => ({
      url: '',
      method: 'POST',
      body: JSON.stringify({
        query: print(findManyPartnerProductQuery),
        variables: {
          filter: args?.filter ?? { isActive: true },
        },
      }),
    }),
    providesTags: ['partnerProducts'],
    transformResponse: (response: PartnerProductsResponse) => {
      return response.data?.findManyPartnerProductQuery ?? [];
    },
  }),
});
